'use client'

import { m, useScroll, useTransform } from 'framer-motion'
import {
  ChevronRight,
  Clock,
  Cookie,
  FileText,
  Info,
  Lock,
  Mail,
  MousePointerClick,
  Server,
  Settings,
} from 'lucide-react'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'

const sections = [
  {
    id: 'what-are-cookies',
    title: '1. Что такое Cookies?',
    icon: Cookie,
    color: 'text-amber-400',
    content: (
      <>
        <p className="mb-6 text-xl leading-relaxed text-gray-300">
          Cookies — это небольшие текстовые файлы, которые сохраняются на вашем устройстве
          (компьютере, планшете или смартфоне) при посещении веб-сайтов. Они помогают сайту работать
          корректно, запоминать ваши настройки и предоставлять вам персонализированный контент.
        </p>
        <p className="text-xl leading-relaxed text-gray-300">
          Мы используем файлы cookie для улучшения работы сайта Цифровой горизонт и повышения
          удобства пользования нашими сервисами.
        </p>
      </>
    ),
  },
  {
    id: 'types',
    title: '2. Типы используемых Cookies',
    icon: Settings,
    color: 'text-blue-400',
    content: (
      <>
        <p className="mb-8 text-xl leading-relaxed text-gray-300">
          Мы используем различные типы файлов cookie для разных целей:
        </p>
        <div className="space-y-6">
          <div className="flex gap-6 rounded-3xl border border-white/5 bg-white/[0.03] p-6">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10">
              <Lock className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h4 className="mb-2 text-xl font-bold text-white">Технические (обязательные)</h4>
              <p className="text-lg leading-relaxed text-gray-400">
                Необходимы для корректной работы сайта. Без них некоторые функции (например,
                отправка форм) могут быть недоступны.
              </p>
            </div>
          </div>
          <div className="flex gap-6 rounded-3xl border border-white/5 bg-white/[0.03] p-6">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-purple-500/20 bg-purple-500/10">
              <Server className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h4 className="mb-2 text-xl font-bold text-white">Аналитические</h4>
              <p className="text-lg leading-relaxed text-gray-400">
                Помогают нам понимать, как пользователи взаимодействуют с сайтом (Яндекс Метрика,
                Google Analytics), чтобы мы могли улучшать его структуру и контент.
              </p>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'management',
    title: '3. Управление Cookies',
    icon: MousePointerClick,
    color: 'text-cyan-400',
    content: (
      <>
        <p className="mb-6 text-xl leading-relaxed text-gray-300">
          Вы можете управлять файлами cookie через настройки вашего браузера. Вы можете
          заблокировать или удалить cookie, однако это может повлиять на работоспособность некоторых
          функций сайта.
        </p>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <p className="mb-4 text-lg font-medium text-white">
            Как отключить cookies в популярных браузерах:
          </p>
          <ul className="space-y-2 text-gray-400">
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              Google Chrome: Настройки → Конфиденциальность и безопасность
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 rounded-full bg-orange-400" />
              Firefox: Настройки → Приватность и защита
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              Safari: Настройки → Конфиденциальность
            </li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 'updates',
    title: '4. Изменения в политике',
    icon: Clock,
    color: 'text-emerald-400',
    content: (
      <p className="text-xl leading-relaxed text-gray-300">
        Мы можем периодически обновлять данную Политику использования файлов cookie. Любые изменения
        вступают в силу с момента их публикации на этой странице. Рекомендуем регулярно проверять
        данную страницу для получения актуальной информации.
      </p>
    ),
  },
]

export default function CookiesPage() {
  const lastUpdate = '09.02.2024'
  const [activeSection, setActiveSection] = useState('what-are-cookies')

  // Scroll progress for visuals
  const { scrollYProgress } = useScroll()
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { threshold: 0.5 },
    )

    sections.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <main className="relative min-h-screen bg-[#020617] px-4 pt-8 pb-32 md:pt-32">
      {/* Scroll Progress Bar */}
      <m.div
        style={{ scaleX }}
        className="fixed top-0 right-0 left-0 z-50 h-1 origin-left bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
      />

      {/* Background Gradient */}
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-blue-900/10 via-purple-900/5 to-transparent" />

      <div className="relative z-10 mx-auto w-[90vw] max-w-[1600px]">
        {/* Header */}
        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-24 flex flex-col items-center text-center"
        >
          <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-sm">
            <Cookie className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-medium tracking-wider text-gray-300 uppercase">
              Политика использования
            </span>
          </div>

          <h1 className="mb-8 text-5xl leading-[1.1] font-bold tracking-tight text-white md:text-7xl lg:text-8xl">
            Файлы
            <br />
            <span className="bg-gradient-to-r from-amber-200 via-orange-400 to-amber-600 bg-clip-text text-transparent">
              Cookies
            </span>
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-6 rounded-2xl border border-white/5 bg-white/[0.03] px-8 py-3 text-lg text-gray-400 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span>
                Обновлено: <strong className="font-medium text-white">{lastUpdate}</strong>
              </span>
            </div>
            <div className="hidden h-1 w-1 rounded-full bg-gray-700 sm:block" />
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span>
                Версия: <strong className="font-medium text-white">1.1</strong>
              </span>
            </div>
          </div>
        </m.div>

        <div className="relative grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-24">
          {/* Left Sidebar - Sticky */}
          <div className="relative lg:col-span-4">
            <div className="sticky top-32 space-y-12">
              {/* Navigation */}
              <nav className="hidden space-y-2 lg:block">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`group flex items-center gap-4 rounded-xl p-4 transition-all duration-300 ${activeSection === section.id ? 'border border-white/10 bg-white/5 shadow-lg' : 'border border-transparent hover:bg-white/[0.02]'}`}
                    onClick={(e) => {
                      e.preventDefault()
                      document
                        .getElementById(section.id)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }}
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${activeSection === section.id ? 'bg-white/10' : 'bg-white/5 group-hover:bg-white/10'}`}
                    >
                      <section.icon
                        className={`h-5 w-5 ${activeSection === section.id ? section.color : 'text-gray-500 group-hover:text-gray-300'}`}
                      />
                    </div>
                    <span
                      className={`text-lg font-medium ${activeSection === section.id ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}
                    >
                      {section.title}
                    </span>
                    {activeSection === section.id && (
                      <ChevronRight className="ml-auto h-5 w-5 text-white opacity-50" />
                    )}
                  </a>
                ))}
              </nav>

              {/* Contact Card */}
              <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-[#0c1220] to-[#05080f] p-8 shadow-2xl">
                <div className="absolute inset-0 bg-blue-500/5 opacity-0 blur-xl transition-opacity duration-700 group-hover:opacity-100" />
                <div className="relative z-10">
                  <h3 className="mb-6 flex items-center gap-3 text-xl font-bold text-white">
                    <Info className="h-6 w-6 text-blue-400" />У вас есть вопросы?
                  </h3>
                  <p className="mb-8 leading-relaxed text-gray-400">
                    Если у вас возникли вопросы касательно использования файлов cookie, свяжитесь с
                    нами.
                  </p>
                  <a
                    href="mailto:nosikov2014@list.ru"
                    className="group/link flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-4 text-white transition-colors hover:bg-white/10 hover:text-blue-400"
                  >
                    <Mail className="h-5 w-5" />
                    <span className="text-lg font-medium">nosikov2014@list.ru</span>
                    <ChevronRight className="ml-auto h-5 w-5 opacity-0 transition-opacity group-hover/link:opacity-100" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Sections */}
          <div className="space-y-12 lg:col-span-8">
            {sections.map((section, idx) => (
              <section key={section.id} id={section.id} className="group scroll-mt-32">
                <m.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="relative overflow-hidden rounded-[40px] border border-white/5 bg-[#0c1220]/60 p-8 backdrop-blur-md transition-colors hover:border-white/10 md:p-12 lg:p-16"
                >
                  <div
                    className={`pointer-events-none absolute top-0 right-0 h-64 w-64 rounded-full bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 blur-[80px] transition-opacity duration-700 group-hover:opacity-100`}
                  />

                  <div className="relative z-10">
                    <div className="mb-8 flex items-center gap-4">
                      <h2 className="text-3xl font-bold text-white md:text-5xl">{section.title}</h2>
                    </div>

                    <div className="prose prose-invert max-w-none">{section.content}</div>
                  </div>
                </m.div>
              </section>
            ))}

            <div className="mt-24 border-t border-white/5 pt-12 text-center">
              <Link
                href="/"
                className="inline-flex items-center gap-2 border-b border-white/20 pb-1 text-lg text-white transition-all hover:border-white hover:text-blue-400"
              >
                Вернуться на главную
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
